import { ListPlus, Plus, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils/cn';

import {
  FIELD_NAME_REGEX,
  FIELD_TYPES,
  MAX_CUSTOM_FIELDS,
  MAX_NAME_LEN,
  TYPE_LABELS,
  blankValueFor,
  emptyBuilderField,
  emptyManualField,
  fieldValueError,
  manualToField,
  type CustomFieldType,
  type ManualFieldDraft,
  type CustomField,
  type CustomFieldTemplate,
  type CustomValue,
  type CustomValues,
} from './customFields';
import { FieldSettingsRow, RequiredCheckbox } from './FieldSettings';

interface CustomFieldsSectionProps {
  templates: CustomFieldTemplate[];
  loading?: boolean;
  templateId: number | null;
  onTemplateChange: (template: CustomFieldTemplate | null) => void;
  values: CustomValues;
  onValueChange: (fieldName: string, value: CustomValue) => void;
  /** Fields added on this contact only; the editor shows when both are given. */
  manualFields?: ManualFieldDraft[];
  onManualChange?: (next: ManualFieldDraft[]) => void;
  /** Shown under the template dropdown (e.g. template being removed on save). */
  templateNotice?: string | null;
  /**
   * Values-only form (agent / team-lead panel): only the contact's own template (shown by name,
   * not swappable) and its saved added fields are shown, values editable; nothing can be added.
   */
  valuesOnly?: boolean;
  disabled?: boolean;
  className?: string;
}

const inputCls = 'input-embossed h-9 w-full rounded-md px-2.5 text-sm disabled:opacity-50';

function FieldInput({
  field,
  value,
  onChange,
  disabled,
}: {
  field: CustomField;
  value: CustomValue | undefined;
  onChange: (v: CustomValue) => void;
  disabled?: boolean;
}) {
  const id = `cf-${field.field_name.replace(/\W+/g, '-')}`;
  switch (field.field_type) {
    case 'Boolean':
      return (
        <label htmlFor={id} className="flex h-9 cursor-pointer items-center gap-2 text-sm text-foreground">
          <input
            id={id}
            type="checkbox"
            className="h-4 w-4 rounded border-border accent-primary"
            checked={value === true}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
          />
          Yes
        </label>
      );
    case 'Textarea':
      return (
        <textarea
          id={id}
          rows={3}
          maxLength={2000}
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="input-embossed w-full rounded-md px-2.5 py-2 text-sm disabled:opacity-50"
        />
      );
    case 'Multi Select': {
      const picked = Array.isArray(value) ? value : [];
      return (
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 rounded-md border border-border/60 px-2.5 py-2">
          {(field.options || []).map((opt) => (
            <label key={opt} className="flex cursor-pointer items-center gap-1.5 text-sm text-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-border accent-primary"
                checked={picked.includes(opt)}
                disabled={disabled}
                onChange={(e) =>
                  onChange(e.target.checked ? [...picked, opt] : picked.filter((p) => p !== opt))
                }
              />
              {opt}
            </label>
          ))}
        </div>
      );
    }
    case 'Integer':
    case 'Float':
      return (
        <input
          id={id}
          type="number"
          step={field.field_type === 'Integer' ? 1 : 'any'}
          min={field.field_min ?? undefined}
          max={field.field_max ?? undefined}
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      );
    case 'Date':
      return (
        <input
          id={id}
          type="date"
          min={field.field_min ? String(field.field_min) : undefined}
          max={field.field_max ? String(field.field_max) : undefined}
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      );
    default:
      return (
        <input
          id={id}
          type="text"
          maxLength={255}
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      );
  }
}

/** FieldInput with its type / min / max problem shown underneath while typing. */
function CheckedFieldInput(props: Parameters<typeof FieldInput>[0]) {
  const error = fieldValueError(props.field, props.value);
  return (
    <>
      <div className={error ? 'rounded-md ring-1 ring-destructive' : undefined}>
        <FieldInput {...props} />
      </div>
      {error && <p className="text-[11px] font-medium text-destructive">{error.charAt(0).toUpperCase() + error.slice(1)}</p>}
    </>
  );
}

/** "Select custom fields" dropdown plus the chosen template's inputs. */
export function CustomFieldsSection({
  templates,
  loading,
  templateId,
  onTemplateChange,
  values,
  onValueChange,
  manualFields,
  onManualChange,
  templateNotice,
  valuesOnly = false,
  disabled,
  className,
}: CustomFieldsSectionProps) {
  const template = templates.find((t) => t.id === templateId) || null;
  const used = (template?.fields.length || 0) + (manualFields?.length || 0);
  const showManual = !!(manualFields && onManualChange) && (!valuesOnly || manualFields.length > 0);
  const showTemplate = !valuesOnly || !!template;
  // Values-only with nothing on the contact: there is nothing to edit here.
  if (valuesOnly && !showManual && !template) return null;

  return (
    <section className={cn('rounded-2xl border border-primary/20 bg-card p-4 shadow-neu-raised', className)}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-2 border-b border-border/60 pb-3">
        <div className="flex items-start gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-inset ring-primary/20">
            <ListPlus className="h-4 w-4" />
          </span>
          <div>
            <h3 className="text-base font-bold text-foreground">Custom Fields</h3>
            <p className="text-xs text-muted-foreground">
              {valuesOnly
                ? 'Extra details saved on this contact. You can update the values.'
                : 'Extra details for this contact: add your own fields, or use a saved template.'}
            </p>
          </div>
        </div>
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold tabular-nums ring-1 ring-inset',
            used >= MAX_CUSTOM_FIELDS
              ? 'bg-amber-100 text-amber-800 ring-amber-300 dark:bg-amber-950/40 dark:text-amber-200'
              : 'bg-primary/10 text-primary ring-primary/20',
          )}
        >
          {used} / {MAX_CUSTOM_FIELDS} fields used
        </span>
      </div>

      <div className="space-y-4">
        {showManual && manualFields && onManualChange && (
          <ManualFieldsEditor
            manual={manualFields}
            onChange={onManualChange}
            templateCount={template?.fields.length || 0}
            disabled={disabled}
            valuesOnly={valuesOnly}
          />
        )}

        {showTemplate && (
        <div className="rounded-xl border border-border/60 bg-muted/20 p-3">
          {valuesOnly && template ? (
            <>
              <h4 className="text-sm font-semibold text-foreground">Template: {template.name}</h4>
              <p className="mb-2 text-[11px] text-muted-foreground">Fields from this contact's template.</p>
            </>
          ) : (
          <>
          <h4 className="text-sm font-semibold text-foreground">Use a custom field template</h4>
          <p className="mb-2 text-[11px] text-muted-foreground">Pick a saved template to fill its fields for this contact.</p>
          <select
            id="custom-field-template"
            aria-label="Custom field template"
            value={templateId ?? ''}
            disabled={disabled || loading}
            onChange={(e) => {
              const id = Number(e.target.value);
              onTemplateChange(templates.find((t) => t.id === id) || null);
            }}
            className={inputCls}
          >
            <option value="">{loading ? 'Loading templates…' : templates.length ? 'None' : 'No templates yet'}</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.fields.length} field{t.fields.length === 1 ? '' : 's'})
              </option>
            ))}
          </select>
          </>
          )}
          {templateNotice && (
            <p className="mt-2 rounded-md bg-amber-50 px-2.5 py-1.5 text-[12px] font-medium text-amber-800 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/30 dark:text-amber-200 dark:ring-amber-900/60">
              {templateNotice}
            </p>
          )}

          {template && (
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              {template.fields.map((field) => (
                <div
                  key={field.field_name}
                  className={cn('space-y-1.5', field.field_type === 'Textarea' || field.field_type === 'Multi Select' ? 'md:col-span-2' : '')}
                >
                  <label
                    htmlFor={`cf-${field.field_name.replace(/\W+/g, '-')}`}
                    className="flex items-center justify-between text-xs font-semibold text-foreground"
                  >
                    <span>
                      {field.field_name}
                      {field.is_required && <span className="text-destructive"> *</span>}
                    </span>
                    <span className="font-normal text-muted-foreground">{TYPE_LABELS[field.field_type]}</span>
                  </label>
                  <CheckedFieldInput
                    field={field}
                    value={values[field.field_name]}
                    onChange={(v) => onValueChange(field.field_name, v)}
                    disabled={disabled}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </div>
    </section>
  );
}

/** "Add field": fields added on this contact only (template-field settings plus the value). */
function ManualFieldsEditor({
  manual,
  onChange,
  templateCount,
  disabled,
  valuesOnly = false,
}: {
  manual: ManualFieldDraft[];
  onChange: (next: ManualFieldDraft[]) => void;
  templateCount: number;
  disabled?: boolean;
  valuesOnly?: boolean;
}) {
  const full = templateCount + manual.length >= MAX_CUSTOM_FIELDS;
  const update = (key: string, patch: Partial<ManualFieldDraft>) =>
    onChange(manual.map((m) => (m.key === key ? { ...m, ...patch } : m)));

  return (
    <div className="rounded-xl border border-border/60 bg-muted/20 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-foreground">{valuesOnly ? 'Contact fields' : 'Add custom fields'}</h4>
          <p className="text-[11px] text-muted-foreground">
            {manual.length
              ? 'Fields added only to this contact.'
              : 'No fields added yet. Use Add field to set up a field (like a template field) and its value for this contact.'}
          </p>
        </div>
        {!valuesOnly && (
        <button
          type="button"
          disabled={disabled || full}
          onClick={() => onChange([...manual, emptyManualField()])}
          title={full ? `Limit of ${MAX_CUSTOM_FIELDS} custom fields reached` : undefined}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-xs font-semibold text-foreground shadow-neu-raised transition hover:bg-muted disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" />
          Add field
        </button>
        )}
      </div>

      {manual.map((m, idx) => {
        const field = manualToField(m);
        return (
          <div key={m.key} className="mt-3 rounded-lg border border-border/60 bg-card/60 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/25">
                Added field #{idx + 1}
              </span>
              {!m.locked && (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onChange(manual.filter((x) => x.key !== m.key))}
                  title="Remove field"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition hover:bg-rose-200 disabled:opacity-50 dark:bg-rose-950/40 dark:text-rose-300"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
              <div className="space-y-1.5 md:col-span-5">
                <label className="text-xs font-semibold text-foreground">
                  Field Name <span className="text-destructive">*</span>
                </label>
                <input
                  value={m.field_name}
                  maxLength={MAX_NAME_LEN}
                  disabled={disabled || m.locked}
                  onChange={(e) => {
                    if (FIELD_NAME_REGEX.test(e.target.value)) update(m.key, { field_name: e.target.value });
                  }}
                  placeholder="e.g. Customer Name"
                  className={inputCls}
                />
              </div>
              <div className="space-y-1.5 md:col-span-4">
                <label className="text-xs font-semibold text-foreground">Type</label>
                <select
                  value={m.field_type}
                  disabled={disabled || m.locked}
                  onChange={(e) => {
                    const type = e.target.value as CustomFieldType;
                    // A new type starts with a clean default/min/max/options, as in the template form.
                    update(m.key, {
                      ...emptyBuilderField(),
                      field_name: m.field_name,
                      is_required: m.is_required,
                      field_type: type,
                      value: blankValueFor(type),
                    });
                  }}
                  className={inputCls}
                >
                  {FIELD_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
              </div>
              {!m.locked && (
                <div className="flex items-end md:col-span-3">
                  <RequiredCheckbox field={m} onChange={(key, value) => update(m.key, { [key]: value })} disabled={disabled} />
                </div>
              )}
            </div>

            {!m.locked && (
              <FieldSettingsRow
                field={m}
                disabled={disabled}
                onChange={(key, value) => {
                  if (key !== 'options') return update(m.key, { [key]: value });
                  // Options changed: drop picked values that are no longer options.
                  const allowed = String(value).split(',').map((o) => o.trim());
                  const picked = Array.isArray(m.value) ? m.value : [];
                  update(m.key, { options: String(value), value: picked.filter((p) => allowed.includes(p)) });
                }}
              />
            )}

            <div className="mt-3 space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Value {m.is_required && m.field_type !== 'Boolean' && <span className="text-destructive">*</span>}
              </label>
              {m.field_type === 'Multi Select' && !field.options?.length ? (
                <p className="text-[11px] text-muted-foreground">Add options first.</p>
              ) : (
                <CheckedFieldInput
                  field={field}
                  value={m.value}
                  onChange={(v) => update(m.key, { value: v })}
                  disabled={disabled}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
